import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";

import MDButton from "components/MDButton";
import { generateQuestionsFromMaterialCall } from "../../redux/api/ai.api";
import {
  AddQuizContentByIdCall,
  AddQuizTitlesCall,
} from "../../redux/api/quizTitle.api";
import { createQuestionCall } from "../../redux/api/quizSystem.api";

/**
 * Two-step wizard that lets admin / uni_admin / prof users upload a piece of
 * material (PDF, audio, PowerPoint, image, generic doc) OR paste text, ask
 * AI to draft N multiple-choice questions, review / edit / delete them,
 * then save the keepers to the right backend (legacy `Quiz` table for the
 * general mock-quiz flow, `QuizQuestionBank` for the professor-exam flow).
 *
 * The two flows share 95% of the UI — only the save call and the secondary
 * "where to land them" field differ — so they live in the same component.
 */

const ACCEPTED_FILE_EXTENSIONS =
  ".pdf,.ppt,.pptx,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.webp,.mp3,.wav,.m4a,.ogg";

const SOURCE_TYPE_LABEL = {
  pdf: "PDF",
  ppt: "PowerPoint",
  image: "Image",
  audio: "Audio",
  document: "Document",
  text: "Pasted text",
  prompt: "AI prompt",
};

const CATEGORY_OPTIONS = [
  { value: "histology", label: "Histology" },
  { value: "embryology", label: "Embryology" },
  { value: "anatomy", label: "Anatomy" },
];

const DIFFICULTY_OPTIONS = [
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];

/* Mirror of the backend's QUIZ_GEN_LANG_LABEL — used to pretty-print the
   detected language when the API didn't include a human label. */
const PROJECT_LANG_LABEL = {
  en: "English",
  pt: "Portuguese",
  es: "Spanish",
  it: "Italian",
  ge: "German",
  nl: "Dutch",
  cn: "Chinese",
  ja: "Japanese",
  ar: "Arabic",
};

const COUNT_MARKS = [5, 10, 15, 20, 30].map((v) => ({
  value: v,
  label: String(v),
}));

const isUUID = (v) =>
  typeof v === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);

const cloneQuestion = (q) => ({
  ...q,
  answers: (q.answers || []).map((a) => ({ ...a })),
});

const ensureFourAnswers = (q) => {
  const a = [...(q.answers || [])];
  while (a.length < 4) a.push({ answer_text: "", is_correct: false });
  return {
    ...q,
    answers: a.slice(0, 4).map((x, i) => ({
      answer_text: String(x?.answer_text ?? "").trim(),
      is_correct: !!x?.is_correct && a.filter((y) => y?.is_correct).indexOf(x) === 0,
      _i: i,
    })),
  };
};

function AIQuizGeneratorWizard({
  open,
  onClose,
  mode,
  lang: initialLang,
  defaultCategory,
  defaultTitleId,
  defaultTitleName,
  existingTitles,
  professorTopics,
  defaultProfessorTopicId,
  onSaved,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const inputBg = isDark
    ? theme.palette.grey?.[800] ?? "#2d2d2d"
    : theme.palette.background.paper;
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: inputBg,
      "& fieldset": { borderColor: theme.palette.divider },
    },
    "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
  };

  // ---------------------------------------------------------------- state
  // The wizard no longer asks the user to pick a language — the backend
  // detects it from the material and returns it in sourceMeta.
  // `fallbackLang` is what we save under if detection fails for some
  // reason (e.g. extraction returned the wrong characters); it comes
  // from the caller's current viewing locale.
  const fallbackLang = initialLang || "en";
  const [step, setStep] = useState(0);
  const [category, setCategory] = useState(defaultCategory || "histology");
  const [titleOption, setTitleOption] = useState(null);
  const [topicId, setTopicId] = useState(defaultProfessorTopicId || "");
  const [defaultDifficulty, setDefaultDifficulty] = useState("medium");
  const [questionType, setQuestionType] = useState("text_only");
  const [count, setCount] = useState(5);
  const [topic, setTopic] = useState("");
  const [file, setFile] = useState(null);
  const [pastedText, setPastedText] = useState("");
  /* "file"   = upload a PDF / PPT / image / audio / doc and extract text
     "text"   = paste raw text the AI should quiz from
     "prompt" = no source material; ask the AI to draft questions about
                the title / topic / category from its own knowledge. */
  const [sourceMode, setSourceMode] = useState("file");

  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [genError, setGenError] = useState("");
  const [sourceMeta, setSourceMeta] = useState(null);

  const [questions, setQuestions] = useState([]); // editable copy
  const [editingId, setEditingId] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);

  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ done: 0, total: 0 });
  const [saveError, setSaveError] = useState("");

  const fileInputRef = useRef(null);

  // Reset everything when reopening so the wizard never shows stale state
  // from a previous run.
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setCategory(defaultCategory || "histology");
    setTopicId(defaultProfessorTopicId || "");
    setDefaultDifficulty("medium");
    setQuestionType("text_only");
    setCount(5);
    setTopic("");
    setFile(null);
    setPastedText("");
    setSourceMode("file");
    setGenerating(false);
    setProgress(0);
    setGenError("");
    setSourceMeta(null);
    setQuestions([]);
    setEditingId(null);
    setEditingDraft(null);
    setSaving(false);
    setSaveProgress({ done: 0, total: 0 });
    setSaveError("");

    /* Pin to the caller-supplied title id even if the name lookup is
       still pending. The save flow uses `id` directly when it's a UUID,
       so an empty `title` doesn't block anything — the user just sees
       a placeholder while the parent screen finishes resolving the
       display name. */
    if (defaultTitleId) {
      setTitleOption({
        id: defaultTitleId,
        title: defaultTitleName || "",
        isNew: false,
      });
    } else {
      setTitleOption(null);
    }
  }, [open, defaultCategory, defaultTitleId, defaultTitleName, defaultProfessorTopicId]);

  const titleOptionsForAutocomplete = useMemo(() => {
    if (mode !== "general") return [];
    const seen = new Set();
    return (existingTitles || [])
      .filter((t) => {
        const id = t?.id;
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .map((t) => ({ id: t.id, title: t.title || "(untitled)", isNew: false }));
  }, [mode, existingTitles]);

  const topicOptionsForUniversity = useMemo(() => {
    if (mode !== "university") return [];
    return (professorTopics || []).map((t) => ({
      id: t.id,
      title: t.title || "(untitled)",
    }));
  }, [mode, professorTopics]);

  // ------------------------------------------------------------- handlers

  const handleClose = () => {
    if (generating || saving) return; // don't allow a mid-flight close
    onClose?.();
  };

  const handlePickFile = () => {
    fileInputRef.current?.click?.();
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setFile(f);
  };

  const handleClearFile = () => setFile(null);

  const handleGenerate = async () => {
    setGenError("");
    setProgress(0);

    if (mode === "general" && !titleOption && !defaultTitleId) {
      setGenError("Pick or type a quiz title for the generated questions.");
      return;
    }
    if (sourceMode === "file" && !file) {
      setGenError("Upload a file first, or switch to Paste text / Prompt only.");
      return;
    }
    if (sourceMode === "text" && !pastedText.trim()) {
      setGenError("Paste some text first, or switch to a different source.");
      return;
    }
    if (
      sourceMode === "prompt" &&
      !topic.trim() &&
      !titleOption?.title &&
      !category
    ) {
      setGenError(
        "Add a quiz title, an instruction, or a category so the AI knows what to ask about.",
      );
      return;
    }

    const form = new FormData();
    if (sourceMode === "file" && file) form.append("file", file);
    if (sourceMode === "text" && pastedText.trim())
      form.append("text", pastedText.trim());
    if (sourceMode === "prompt") form.append("source_mode", "prompt");
    if (titleOption?.title) form.append("quiz_title", titleOption.title);
    form.append("count", String(count));
    // Send the viewing locale as a hint only — backend uses it solely as
    // a fallback if it cannot detect a language from the material. In
    // prompt-only mode there's no source text to detect from, so the
    // backend writes the questions directly in this locale.
    form.append("lang", fallbackLang);
    if (topic.trim()) form.append("topic", topic.trim());
    if (mode === "general" && category) form.append("category", category);
    if (mode === "university") form.append("category", "university");
    form.append("question_type", questionType);

    setGenerating(true);
    try {
      const res = await generateQuestionsFromMaterialCall(form, setProgress);
      const status = typeof res?.status === "number" ? res.status : 200;
      if (status >= 400) {
        setGenError(
          res?.message ||
            "Could not generate questions. Try a different file or paste more text.",
        );
        return;
      }
      const list = Array.isArray(res?.questions) ? res.questions : [];
      if (!list.length) {
        setGenError("The AI didn't return any questions. Try again.");
        return;
      }
      // Stamp a stable per-row id (the AI ids like 'q1' are not unique
      // across regenerations) so React keys + inline edit work cleanly.
      const stamped = list.map((q, i) => ({
        ...ensureFourAnswers(q),
        rowId: `${Date.now()}-${i}`,
      }));
      setQuestions(stamped);
      setSourceMeta(res?.sourceMeta || null);
      setStep(1);
    } catch (e) {
      console.error("[AIQuizGenerator] generate failed", e);
      setGenError(e?.message || "Something went wrong. Try again.");
    } finally {
      setGenerating(false);
      setProgress(0);
    }
  };

  const handleRegenerate = () => {
    setStep(0);
    setQuestions([]);
    setEditingId(null);
    setEditingDraft(null);
    setSourceMeta(null);
    setSaveError("");
    setSaveProgress({ done: 0, total: 0 });
  };

  const handleStartEdit = (q) => {
    setEditingId(q.rowId);
    setEditingDraft(cloneQuestion(q));
  };
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingDraft(null);
  };
  const handleCommitEdit = () => {
    if (!editingDraft) return handleCancelEdit();
    setQuestions((prev) =>
      prev.map((q) => (q.rowId === editingId ? { ...editingDraft } : q)),
    );
    setEditingId(null);
    setEditingDraft(null);
  };

  const handleDelete = (rowId) => {
    setQuestions((prev) => prev.filter((q) => q.rowId !== rowId));
    if (editingId === rowId) handleCancelEdit();
  };

  const updateEditingField = (field, value) => {
    setEditingDraft((d) => (d ? { ...d, [field]: value } : d));
  };
  const updateEditingAnswerText = (idx, value) => {
    setEditingDraft((d) => {
      if (!d) return d;
      const a = d.answers.map((x, i) =>
        i === idx ? { ...x, answer_text: value } : x,
      );
      return { ...d, answers: a };
    });
  };
  const updateEditingCorrect = (idx) => {
    setEditingDraft((d) => {
      if (!d) return d;
      const a = d.answers.map((x, i) => ({ ...x, is_correct: i === idx }));
      return { ...d, answers: a };
    });
  };

  /* Resolve the locale we should persist under — prefer the language the
     backend detected from the material; only fall back to the caller's
     viewing lang if detection failed for some reason. Question CONTENT
     is in the detected language, so the row must be saved against that
     same lang slot or it'll show up as a mismatched translation. */
  const saveLang = sourceMeta?.detectedLanguage?.code || fallbackLang;

  // ---------------------------------------------- save (general flow)

  const saveAsGeneralQuestion = async (q, titleId) => {
    const correctIdx = q.answers.findIndex((a) => a.is_correct);
    const rightAnswer = String(correctIdx >= 0 ? correctIdx + 1 : 1);
    const level =
      q.difficulty === "easy" ? "0" : q.difficulty === "hard" ? "2" : "1";
    const payload = {
      title_id: titleId,
      lang: saveLang,
      title: q.title || "",
      content: q.question_content || "",
      answer_a: q.answers[0]?.answer_text || "",
      answer_b: q.answers[1]?.answer_text || "",
      answer_c: q.answers[2]?.answer_text || "",
      answer_d: q.answers[3]?.answer_text || "",
      right_answer: rightAnswer,
      level,
    };
    return AddQuizContentByIdCall(payload);
  };

  // ---------------------------------------------- save (university flow)

  const saveAsUniversityQuestion = async (q) => {
    const payload = {
      title: q.title || "",
      question_content: q.question_content || "",
      difficulty: q.difficulty || defaultDifficulty || "medium",
      question_type: questionType,
      answers: q.answers.map((a) => ({
        answer_text: a.answer_text || "",
        is_correct: !!a.is_correct,
      })),
    };
    if (topicId) payload.professor_topic_id = topicId;
    return createQuestionCall(payload);
  };

  const ensureGeneralTitleId = async () => {
    if (!titleOption) return null;
    if (titleOption.id && isUUID(titleOption.id) && !titleOption.isNew) {
      return titleOption.id;
    }
    // Newly-typed title — create it under the detected language so the
    // title and its questions live in the same translation slot. The
    // admin can add translations for other locales later.
    const res = await AddQuizTitlesCall({
      title: titleOption.title,
      lang: saveLang,
      type: category,
    });
    const newId = res?.data?.id || res?.id;
    if (!newId) {
      throw new Error(res?.message || "Failed to create new quiz title.");
    }
    return newId;
  };

  const handleSaveAll = async () => {
    if (editingId) handleCommitEdit();
    if (!questions.length) {
      setSaveError("Nothing to save — every question was removed.");
      return;
    }
    setSaveError("");
    setSaving(true);
    setSaveProgress({ done: 0, total: questions.length });

    try {
      let titleId = null;
      if (mode === "general") {
        titleId = await ensureGeneralTitleId();
      }

      let failures = 0;
      for (let i = 0; i < questions.length; i += 1) {
        const q = questions[i];
        try {
          if (mode === "general") {
            await saveAsGeneralQuestion(q, titleId);
          } else {
            await saveAsUniversityQuestion(q);
          }
        } catch (perItemErr) {
          console.error("[AIQuizGenerator] save item failed", perItemErr);
          failures += 1;
        }
        setSaveProgress({ done: i + 1, total: questions.length });
      }

      const saved = questions.length - failures;
      onSaved?.({
        savedCount: saved,
        failedCount: failures,
        titleId,
        lang: saveLang,
        detectedLanguage: sourceMeta?.detectedLanguage || null,
      });
      onClose?.();
    } catch (e) {
      console.error("[AIQuizGenerator] save failed", e);
      setSaveError(e?.message || "Failed to save questions.");
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------- render

  const correctIndexOf = (q) =>
    Math.max(
      0,
      (q.answers || []).findIndex((a) => a.is_correct),
    );

  const sourceLabel = sourceMeta
    ? `${SOURCE_TYPE_LABEL[sourceMeta.sourceType] || "Source"}${
        sourceMeta.fileName ? ` · ${sourceMeta.fileName}` : ""
      }`
    : null;

  const detectedLangChipLabel = sourceMeta?.detectedLanguage
    ? `Detected language: ${
        sourceMeta.detectedLanguage.label ||
        PROJECT_LANG_LABEL[sourceMeta.detectedLanguage.code] ||
        sourceMeta.detectedLanguage.code
      }`
    : null;

  const dialogTitle = (
    <Stack direction="row" alignItems="center" spacing={1.2}>
      <AutoAwesomeRoundedIcon sx={{ color: "primary.main" }} />
      <span>Generate quiz questions with AI</span>
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { bgcolor: "background.default" } }}
    >
      <DialogTitle sx={{ pb: 0 }}>{dialogTitle}</DialogTitle>
      <DialogContent dividers sx={{ pt: 2.5 }}>
        {/* ────────────────────── step 1: configure ───────────────────── */}
        {step === 0 && (
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary">
              Choose how you want to seed the AI: upload a file to extract from,
              paste your own text, or just describe a topic and let the AI draft
              questions from its medical knowledge. You'll review and edit every
              question before anything is saved.
            </Typography>

            {/* ───── source-mode picker — three rich cards ───── */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ width: "100%" }}
            >
              {[
                {
                  id: "file",
                  Icon: UploadFileRoundedIcon,
                  title: "Upload material",
                  desc: "PDF, PowerPoint, doc, image, or audio. We extract text + draft questions.",
                },
                {
                  id: "text",
                  Icon: NotesRoundedIcon,
                  title: "Paste text",
                  desc: "Paste lecture notes or any passage and quiz directly from it.",
                },
                {
                  id: "prompt",
                  Icon: LightbulbRoundedIcon,
                  title: "Prompt only",
                  desc: "No source needed — describe a topic and the AI drafts from medical knowledge.",
                },
              ].map(({ id, Icon, title, desc }) => {
                const active = sourceMode === id;
                return (
                  <Paper
                    key={id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSourceMode(id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSourceMode(id);
                      }
                    }}
                    elevation={0}
                    sx={{
                      flex: 1,
                      p: 2,
                      borderRadius: 2,
                      cursor: "pointer",
                      border: "2px solid",
                      borderColor: active
                        ? theme.palette.primary.main
                        : theme.palette.divider,
                      bgcolor: active
                        ? alpha(theme.palette.primary.main, 0.08)
                        : "background.paper",
                      transition:
                        "border-color 0.18s ease, background-color 0.18s ease, transform 0.18s ease",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        transform: "translateY(-1px)",
                      },
                      "&:focus-visible": {
                        outline: "none",
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}`,
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1.5,
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: active
                            ? alpha(theme.palette.primary.main, 0.18)
                            : alpha(theme.palette.text.primary, 0.06),
                          color: active
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                        }}
                      >
                        <Icon fontSize="small" />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.75}
                          sx={{ mb: 0.5 }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{ color: "text.primary" }}
                          >
                            {title}
                          </Typography>
                          {active && (
                            <CheckRoundedIcon
                              fontSize="small"
                              sx={{ color: "primary.main" }}
                            />
                          )}
                        </Stack>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            display: "block",
                            lineHeight: 1.4,
                          }}
                        >
                          {desc}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                borderStyle: "dashed",
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              }}
            >
              {sourceMode === "file" && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", sm: "center" }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<UploadFileRoundedIcon />}
                    onClick={handlePickFile}
                    sx={{ textTransform: "none" }}
                  >
                    {file ? "Replace file" : "Upload material"}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_FILE_EXTENSIONS}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  {file ? (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flex={1}
                    >
                      <DescriptionRoundedIcon sx={{ color: "primary.main" }} />
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={file.name}
                      >
                        {file.name}{" "}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          ({Math.round((file.size || 0) / 1024)} KB)
                        </Typography>
                      </Typography>
                      <IconButton size="small" onClick={handleClearFile}>
                        <CloseRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Accepted: PDF, PPT/PPTX, DOC, TXT, JPG/PNG, MP3/M4A/WAV
                    </Typography>
                  )}
                </Stack>
              )}

              {sourceMode === "text" && (
                <TextField
                  value={pastedText}
                  onChange={(e) => setPastedText(e.target.value)}
                  placeholder="Paste lecture notes, chapter excerpts, or any text you want quizzed…"
                  multiline
                  minRows={6}
                  maxRows={14}
                  fullWidth
                  sx={inputSx}
                />
              )}

              {sourceMode === "prompt" && (
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LightbulbRoundedIcon sx={{ color: "primary.main" }} />
                    <Typography variant="subtitle2">
                      Prompt-only generation
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    No file or pasted text is needed. The AI will draft
                    questions about the quiz title{" "}
                    <strong>
                      {titleOption?.title ||
                        defaultTitleName ||
                        "(pick a title below)"}
                    </strong>
                    {category ? ` in the ${category} domain` : ""} from standard
                    medical-school knowledge. Add an extra instruction below to
                    steer it (e.g. "focus on clinical correlations" or "ask
                    about microscopic anatomy"). Every question is reviewed by
                    you before it's saved.
                  </Typography>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Because there's no source text, the AI relies on its own
                    training. Always proof-read each question before saving.
                  </Alert>
                </Stack>
              )}
            </Paper>

            {/* Number of questions */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                How many questions?{" "}
                <Typography component="span" color="primary.main" fontWeight={700}>
                  {count}
                </Typography>
              </Typography>
              <Slider
                value={count}
                min={1}
                max={30}
                step={1}
                marks={COUNT_MARKS}
                onChange={(_, v) => setCount(Array.isArray(v) ? v[0] : v)}
                valueLabelDisplay="auto"
              />
            </Box>

            {/* Row of selectors. We anchor everything to the TOP so the
                Autocomplete's helperText (which makes it taller) doesn't
                stretch the sibling Selects vertically — they'd otherwise
                inherit the bigger height and look squeezed. Each control
                also gets an explicit fixed input height so they line up
                cleanly. */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems="flex-start"
            >
              {mode === "general" && (
                <>
                  <FormControl
                    sx={{
                      minWidth: 180,
                      "& .MuiInputBase-root": { height: 56 },
                    }}
                  >
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={category}
                      label="Category"
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      {CATEGORY_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={titleOptionsForAutocomplete}
                    value={titleOption}
                    onChange={(_, val) => setTitleOption(val)}
                    onInputChange={(_, val, reason) => {
                      if (reason !== "input") return;
                      if (!val) {
                        setTitleOption(null);
                        return;
                      }
                      const trimmed = val.trim();
                      const match = titleOptionsForAutocomplete.find(
                        (o) => o.title.toLowerCase() === trimmed.toLowerCase(),
                      );
                      setTitleOption(
                        match
                          ? match
                          : { id: null, title: trimmed, isNew: true },
                      );
                    }}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option?.title || ""
                    }
                    isOptionEqualToValue={(o, v) =>
                      (o?.id && v?.id && o.id === v.id) ||
                      (o?.title || "").toLowerCase() ===
                        (v?.title || "").toLowerCase()
                    }
                    freeSolo
                    selectOnFocus
                    clearOnBlur={false}
                    disabled={!!defaultTitleId}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id || `new-${option.title}`}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          width="100%"
                        >
                          <span style={{ flex: 1 }}>{option.title}</span>
                          {option.isNew && (
                            <Chip
                              size="small"
                              color="primary"
                              label="New"
                              sx={{ height: 20 }}
                            />
                          )}
                        </Stack>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Quiz title (pick or type a new one)"
                        sx={inputSx}
                        helperText={
                          defaultTitleId
                            ? "Saving questions into this title"
                            : titleOption?.isNew
                              ? "A new title will be created when you save"
                              : " "
                        }
                      />
                    )}
                  />
                </>
              )}

              {mode === "university" && (
                <>
                  {topicOptionsForUniversity.length > 0 && (
                    <FormControl
                      sx={{
                        minWidth: 240,
                        flex: 1,
                        "& .MuiInputBase-root": { height: 56 },
                      }}
                    >
                      <InputLabel>Topic (optional)</InputLabel>
                      <Select
                        value={topicId}
                        label="Topic (optional)"
                        onChange={(e) => setTopicId(e.target.value)}
                      >
                        <MenuItem value="">
                          <em>No topic</em>
                        </MenuItem>
                        {topicOptionsForUniversity.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <FormControl
                    sx={{
                      minWidth: 160,
                      "& .MuiInputBase-root": { height: 56 },
                    }}
                  >
                    <InputLabel>Default difficulty</InputLabel>
                    <Select
                      value={defaultDifficulty}
                      label="Default difficulty"
                      onChange={(e) => setDefaultDifficulty(e.target.value)}
                    >
                      {DIFFICULTY_OPTIONS.map((d) => (
                        <MenuItem key={d.value} value={d.value}>
                          {d.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </Stack>

            {/* Language note: only meaningful when there's a source to
                detect from. In prompt-only mode the questions are written
                directly in the current viewing locale. */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                py: 1,
                px: 1.5,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.info.main, 0.08),
                border: 1,
                borderColor: alpha(theme.palette.info.main, 0.2),
              }}
            >
              <TranslateRoundedIcon
                sx={{ color: "info.main" }}
                fontSize="small"
              />
              <Typography variant="body2" color="text.primary">
                {sourceMode === "prompt" ? (
                  <>
                    Prompt-only mode writes questions in the{" "}
                    <strong>current viewing language</strong> ({fallbackLang}).
                  </>
                ) : (
                  <>
                    <strong>Language is auto-detected</strong> from your
                    material — the questions will be written in the same
                    language and saved under the matching locale.
                  </>
                )}
              </Typography>
            </Stack>

            <TextField
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              label={
                sourceMode === "prompt"
                  ? "What should the AI ask about? (recommended)"
                  : "Extra instruction for the AI (optional)"
              }
              placeholder={
                sourceMode === "prompt"
                  ? "e.g. ask about microscopic anatomy of the liver, focus on portal triad and sinusoids…"
                  : "e.g. focus on clinical relevance, or target 1st-year students…"
              }
              fullWidth
              multiline={sourceMode === "prompt"}
              minRows={sourceMode === "prompt" ? 2 : 1}
              sx={inputSx}
            />

            {genError && <Alert severity="error">{genError}</Alert>}
            {generating && (
              <Box>
                <LinearProgress
                  variant={progress > 0 && progress < 1 ? "determinate" : "indeterminate"}
                  value={Math.round(progress * 100)}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {progress > 0 && progress < 1
                    ? `Uploading… ${Math.round(progress * 100)}%`
                    : sourceMode === "prompt"
                      ? "Drafting questions from the prompt — usually 10-20 seconds…"
                      : "Generating questions — this can take 30-90 seconds for large files…"}
                </Typography>
              </Box>
            )}
          </Stack>
        )}

        {/* ────────────────────── step 2: review ────────────────────── */}
        {step === 1 && (
          <Stack spacing={2}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Chip
                color="primary"
                size="small"
                label={`${questions.length} question${questions.length === 1 ? "" : "s"} ready`}
              />
              {detectedLangChipLabel && (
                <Tooltip title="Detected automatically from your source material. Questions will be saved under this locale.">
                  <Chip
                    size="small"
                    color="info"
                    variant="outlined"
                    icon={<TranslateRoundedIcon />}
                    label={detectedLangChipLabel}
                  />
                </Tooltip>
              )}
              {sourceLabel && (
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<DescriptionRoundedIcon />}
                  label={sourceLabel}
                />
              )}
              {sourceMeta?.truncated && (
                <Tooltip title="The source was longer than 60,000 characters; only the first portion was used.">
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label="Source truncated"
                  />
                </Tooltip>
              )}
              <Box sx={{ flex: 1 }} />
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                startIcon={<RestartAltRoundedIcon />}
                onClick={handleRegenerate}
              >
                Regenerate
              </MDButton>
            </Stack>

            {sourceMeta?.warnings?.length > 0 && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                {sourceMeta.warnings.join(" ")}
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary">
              Review each question, edit any wording, and remove the ones you
              don't want. The kept questions will be saved to{" "}
              {mode === "general"
                ? `the "${titleOption?.title || "selected title"}" quiz title.`
                : "your question bank."}
            </Typography>

            <Stack spacing={1.5}>
              {questions.map((q, idx) => {
                const isEditing = editingId === q.rowId;
                const draft = isEditing ? editingDraft : q;
                const correctIdx = correctIndexOf(draft);

                return (
                  <Paper
                    key={q.rowId}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderColor: isEditing
                        ? theme.palette.primary.main
                        : theme.palette.divider,
                      bgcolor: isEditing
                        ? alpha(theme.palette.primary.main, 0.04)
                        : "background.paper",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      sx={{ mb: 1 }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ minWidth: 28, mt: 0.5 }}
                      >
                        {idx + 1}.
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        {isEditing ? (
                          <Stack spacing={1.2}>
                            <TextField
                              size="small"
                              label="Title (short)"
                              value={draft.title || ""}
                              onChange={(e) =>
                                updateEditingField("title", e.target.value)
                              }
                              sx={inputSx}
                            />
                            <TextField
                              size="small"
                              label="Question"
                              value={draft.question_content || ""}
                              onChange={(e) =>
                                updateEditingField(
                                  "question_content",
                                  e.target.value,
                                )
                              }
                              multiline
                              minRows={2}
                              sx={inputSx}
                            />
                            <FormControl size="small" sx={{ maxWidth: 200 }}>
                              <InputLabel>Difficulty</InputLabel>
                              <Select
                                value={draft.difficulty || "medium"}
                                label="Difficulty"
                                onChange={(e) =>
                                  updateEditingField(
                                    "difficulty",
                                    e.target.value,
                                  )
                                }
                              >
                                {DIFFICULTY_OPTIONS.map((d) => (
                                  <MenuItem key={d.value} value={d.value}>
                                    {d.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Stack>
                        ) : (
                          <>
                            {draft.title && (
                              <Typography
                                variant="caption"
                                color="primary.main"
                                fontWeight={600}
                                sx={{ display: "block" }}
                              >
                                {draft.title}
                              </Typography>
                            )}
                            <Typography variant="body1" sx={{ mb: 0.5 }}>
                              {draft.question_content}
                            </Typography>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Difficulty: ${draft.difficulty || "medium"}`}
                              sx={{ mt: 0.5 }}
                            />
                          </>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        {isEditing ? (
                          <>
                            <Tooltip title="Save edits">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={handleCommitEdit}
                              >
                                <CheckRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Discard edits">
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                              >
                                <CloseRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleStartEdit(q)}
                                disabled={!!editingId}
                              >
                                <EditRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(q.rowId)}
                              >
                                <DeleteOutlineRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </Stack>

                    {isEditing ? (
                      <RadioGroup
                        value={String(correctIdx)}
                        onChange={(e) =>
                          updateEditingCorrect(Number(e.target.value))
                        }
                      >
                        <Stack spacing={0.75}>
                          {draft.answers.map((ans, i) => (
                            <Stack
                              key={i}
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <FormControlLabel
                                value={String(i)}
                                control={<Radio size="small" />}
                                label=""
                                sx={{ m: 0 }}
                              />
                              <TextField
                                size="small"
                                value={ans.answer_text}
                                onChange={(e) =>
                                  updateEditingAnswerText(i, e.target.value)
                                }
                                fullWidth
                                sx={inputSx}
                                placeholder={`Option ${String.fromCharCode(65 + i)}`}
                              />
                            </Stack>
                          ))}
                        </Stack>
                      </RadioGroup>
                    ) : (
                      <Stack spacing={0.5}>
                        {draft.answers.map((ans, i) => {
                          const correct = i === correctIdx;
                          return (
                            <Stack
                              key={i}
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: "50%",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  bgcolor: correct
                                    ? alpha(theme.palette.success.main, 0.18)
                                    : alpha(theme.palette.text.primary, 0.06),
                                  color: correct
                                    ? theme.palette.success.main
                                    : theme.palette.text.secondary,
                                  fontWeight: 700,
                                  fontSize: 12,
                                }}
                              >
                                {String.fromCharCode(65 + i)}
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  flex: 1,
                                  color: correct
                                    ? theme.palette.success.main
                                    : "text.primary",
                                  fontWeight: correct ? 600 : 400,
                                }}
                              >
                                {ans.answer_text || (
                                  <em style={{ opacity: 0.6 }}>(empty)</em>
                                )}
                              </Typography>
                              {correct && (
                                <Chip
                                  size="small"
                                  color="success"
                                  label="Correct"
                                  sx={{ height: 20 }}
                                />
                              )}
                            </Stack>
                          );
                        })}
                      </Stack>
                    )}
                  </Paper>
                );
              })}
              {!questions.length && (
                <Alert severity="warning">
                  All generated questions were removed. Click{" "}
                  <strong>Regenerate</strong> to try again.
                </Alert>
              )}
            </Stack>

            {saveError && <Alert severity="error">{saveError}</Alert>}
            {saving && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={
                    saveProgress.total
                      ? (saveProgress.done / saveProgress.total) * 100
                      : 0
                  }
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  Saving {saveProgress.done} of {saveProgress.total}…
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={generating || saving}>
          Cancel
        </Button>
        {step === 0 && (
          <MDButton
            color="info"
            onClick={handleGenerate}
            disabled={generating}
            startIcon={
              generating ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <AutoAwesomeRoundedIcon />
              )
            }
          >
            {generating ? "Generating…" : `Generate ${count} question${count === 1 ? "" : "s"}`}
          </MDButton>
        )}
        {step === 1 && (
          <MDButton
            color="success"
            onClick={handleSaveAll}
            disabled={saving || !questions.length || !!editingId}
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <CheckRoundedIcon />
              )
            }
          >
            {saving
              ? `Saving ${saveProgress.done}/${saveProgress.total}…`
              : `Save ${questions.length} question${questions.length === 1 ? "" : "s"}`}
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

AIQuizGeneratorWizard.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** "general" = legacy mock-quiz titles + per-title questions
   *  "university" = professor question bank */
  mode: PropTypes.oneOf(["general", "university"]).isRequired,
  lang: PropTypes.string,
  /** general mode default category */
  defaultCategory: PropTypes.string,
  /** general mode — pin the generated batch to an existing title */
  defaultTitleId: PropTypes.string,
  defaultTitleName: PropTypes.string,
  /** general mode — array of { id, title } already loaded by the caller */
  existingTitles: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, title: PropTypes.string }),
  ),
  /** university mode — array of { id, title } professor topics */
  professorTopics: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, title: PropTypes.string }),
  ),
  defaultProfessorTopicId: PropTypes.string,
  /** Called after a save attempt completes (success or partial) */
  onSaved: PropTypes.func,
};

AIQuizGeneratorWizard.defaultProps = {
  lang: "en",
  defaultCategory: "histology",
  defaultTitleId: undefined,
  defaultTitleName: undefined,
  existingTitles: [],
  professorTopics: [],
  defaultProfessorTopicId: "",
  onSaved: undefined,
};

export default AIQuizGeneratorWizard;
